import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { CheckCircle, Download, Music, Youtube, Mail, Gift } from 'lucide-react';
import Footer from './Footer';

const SoundtrackSuccessPage = () => {
  const [paymentStatus, setPaymentStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [paymentId, setPaymentId] = useState('');
  const [email, setEmail] = useState('');
  const [optInSuccess, setOptInSuccess] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const verifyPayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      // Support both session_id (cs_...) and payment_intent (pi_...)
      const sessionId = urlParams.get('session_id');
      const paymentIntent = urlParams.get('payment_intent');
      const pid = sessionId || paymentIntent;
      
      if (!pid) {
        setPaymentStatus('error');
        return;
      }
      
      setPaymentId(pid);

      try {
        // Use the new unified verify-payment endpoint for pi_ IDs
        // Use checkout-status for cs_ IDs (legacy support)
        let endpoint;
        if (pid.startsWith('pi_')) {
          endpoint = `${API_URL}/api/verify-payment/${pid}`;
        } else {
          endpoint = `${API_URL}/api/checkout-status/${pid}`;
        }
        
        const response = await fetch(endpoint);
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          setPaymentData(data);
        } else {
          setPaymentStatus('pending');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setPaymentStatus('error');
      }
    };

    verifyPayment();
  }, [API_URL]);

  const handleOptIn = async (e) => {
    e.preventDefault();
    
    if (!email) {
      alert('Please enter your email address');
      return;
    }

    // TODO: Integrate with your email service (e.g., Mailchimp, ConvertKit, etc.)
    // For now, just show success message
    setOptInSuccess(true);
    
    console.log('Email opt-in:', email);
    // You can add backend API call here to store the email
  };

  if (paymentStatus === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-red-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-white text-xl">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'error' || paymentStatus === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-red-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Payment Verification Issue</h2>
          <p className="text-gray-300 mb-6">
            We're having trouble verifying your payment. Please contact support at info@eBlackBookListings.com with your Payment ID.
          </p>
          <Button onClick={() => navigate('/')} className="bg-white text-black">
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  // Use the paymentId from state (supports both cs_ and pi_)
  const downloadId = paymentId;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-red-900">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-sm py-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => navigate('/')}
            className="text-white hover:text-red-500 transition-colors flex items-center gap-2"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-12">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Thank You for Your Support! 🎉
            </h1>
            <p className="text-xl text-gray-300 mb-2">
              Your purchase helps fuel the movement and supports independent artistry.
            </p>
            <p className="text-lg text-yellow-400 font-semibold">
              Mindset Shift - The Official Soundtrack is now yours forever.
            </p>
          </div>

          {/* Download Section */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-8 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Download className="w-8 h-8 text-yellow-400" />
              Your Downloads
            </h2>

            <div className="space-y-4">
              {/* Soundtrack Download */}
              <div className="bg-black/30 rounded-xl p-6 border border-yellow-500/30">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <Music className="w-10 h-10 text-yellow-400" />
                    <div>
                      <h3 className="text-white font-bold text-lg">Full Album (11 Tracks)</h3>
                      <p className="text-gray-400 text-sm">High-quality MP3 files</p>
                    </div>
                  </div>
                  <a
                    href={`${API_URL}/api/download-file/${downloadId}/mindset_shift_soundtrack_full_album.zip`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                      <Download className="w-5 h-5 mr-2" />
                      Download Album
                    </Button>
                  </a>
                </div>
              </div>

              {/* Lyric Sheet Download */}
              <div className="bg-black/30 rounded-xl p-6 border border-purple-500/30">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <Download className="w-10 h-10 text-purple-400" />
                    <div>
                      <h3 className="text-white font-bold text-lg">Official Lyric Sheet</h3>
                      <p className="text-gray-400 text-sm">PDF format</p>
                    </div>
                  </div>
                  <a
                    href={`${API_URL}/api/download-file/${downloadId}/mindset_shift_lyric_sheet.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-purple-500 hover:bg-purple-600 text-white font-bold">
                      <Download className="w-5 h-5 mr-2" />
                      Download Lyrics
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-gray-300">
                💡 <strong>Tip:</strong> These download links are valid for 24 hours. 
                Save your files to your device now. If you need access later, email us at info@eBlackBookListings.com
              </p>
            </div>
          </div>

          {/* Jersey Mindset Auntie Video */}
          <div className="bg-gradient-to-br from-red-900/50 to-black/50 backdrop-blur-md rounded-2xl p-8 mb-8 border border-red-500/30">
            <div className="flex items-center gap-4 mb-6">
              <Youtube className="w-12 h-12 text-red-500" />
              <div>
                <h2 className="text-2xl font-black text-white">
                  Meet Jersey Mindset Auntie
                </h2>
                <p className="text-gray-300">
                  Your Guide to Transformation
                </p>
              </div>
            </div>

            <p className="text-gray-200 mb-6 leading-relaxed">
              The music opens your heart. Now let Jersey Mindset Auntie guide your mind. 
              Watch daily videos that reinforce the mindset shifts you need to create real opportunity.
            </p>

            <div className="aspect-video bg-black rounded-xl overflow-hidden mb-6">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/T-RXF5lkF7A"
                title="Jersey Mindset Auntie"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>

            <div className="flex gap-4 flex-wrap">
              <a
                href="https://youtube.com/shorts/T-RXF5lkF7A?si=rYNc54mNvTpOxHgr"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-red-600 hover:bg-red-700 text-white">
                  <Youtube className="w-5 h-5 mr-2" />
                  Subscribe for More
                </Button>
              </a>
              <Button 
                onClick={() => navigate('/mindset-shift-ecosystem')}
                variant="outline"
                className="border-red-500 text-red-300 hover:bg-red-900/50"
              >
                Explore the Full eCoSystem
              </Button>
            </div>
          </div>

          {/* Optional Email Opt-In */}
          <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 backdrop-blur-md rounded-2xl p-8 border border-purple-500/30">
            <div className="flex items-center gap-4 mb-6">
              <Gift className="w-12 h-12 text-pink-400" />
              <div>
                <h2 className="text-2xl font-black text-white">
                  Want Exclusive Bonus Content?
                </h2>
                <p className="text-gray-300">
                  (Optional - Your choice!)
                </p>
              </div>
            </div>

            {!optInSuccess ? (
              <>
                <p className="text-gray-200 mb-6 leading-relaxed">
                  Get early access to <strong>remix versions</strong>, <strong>pre-production demos</strong>, 
                  and be the first to know about future projects. No spam, just exclusive content for true supporters.
                </p>

                <form onSubmit={handleOptIn} className="flex gap-4 flex-col sm:flex-row">
                  <div className="flex-1">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full px-4 py-3 rounded-lg bg-black/50 text-white border border-purple-500/50 focus:outline-none focus:border-purple-500"
                      required
                    />
                  </div>
                  <Button type="submit" className="bg-pink-600 hover:bg-pink-700 text-white font-bold">
                    <Mail className="w-5 h-5 mr-2" />
                    Yes, Send Me Bonuses!
                  </Button>
                </form>

                <p className="text-xs text-gray-400 mt-4">
                  * This is completely optional. You already have everything you purchased above.
                </p>
              </>
            ) : (
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-6 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <p className="text-white font-bold text-lg mb-2">
                  You're In! 🎉
                </p>
                <p className="text-gray-300">
                  Check your inbox for your first bonus. Welcome to the inner circle!
                </p>
              </div>
            )}
          </div>

          {/* Support Contact */}
          <div className="mt-8 text-center">
            <p className="text-gray-400 text-sm">
              Questions or need help? Email us at{' '}
              <a href="mailto:info@eBlackBookListings.com" className="text-yellow-400 hover:text-yellow-300">
                info@eBlackBookListings.com
              </a>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SoundtrackSuccessPage;
